import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CX_TONE = { low: "text-emerald-600 bg-emerald-50", moderate: "text-amber-600 bg-amber-50", high: "text-rose-600 bg-rose-50" };
const ATYPE = { yes_no: "Yes / No", text: "Text", number: "Number", currency: "Amount", date: "Date", select: "Select", table: "Table", multiline: "Notes" };

function Chip({ icon, children, tone = "text-slate-600 bg-slate-100" }) {
  const I = icon ? Icons[icon] : null;
  return <span className={cn("inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full", tone)}>{I && <I className="h-3 w-3" />}{children}</span>;
}

function ItemRow({ item, docTypes, addons, profileLabels }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`om-item-${item.key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-3 p-3 text-left hover:bg-slate-50/60">
        <Icons.ChevronRight className={cn("h-4 w-4 text-slate-400 mt-0.5 transition-transform shrink-0", open && "rotate-90")} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#171B2E]">{item.label}
            {item.required && <span className="text-rose-400 ml-1" title="Required">*</span>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <Chip>{ATYPE[item.answer_type] || item.answer_type}</Chip>
            {item.profile_field && <Chip icon="ClipboardList" tone="text-blue-600 bg-blue-50">{profileLabels[item.profile_field] || item.profile_field}</Chip>}
            {item.doc_trigger.map((d) => <Chip key={d} icon="FileText" tone="text-indigo-600 bg-indigo-50">{docTypes[d] || d}</Chip>)}
            {item.addon_trigger.map((a) => <Chip key={a} icon="PlusCircle" tone="text-fuchsia-600 bg-fuchsia-50">{addons[a] || a}</Chip>)}
            {item.complexity && <Chip icon="Gauge" tone={CX_TONE[item.complexity]}>{item.complexity} complexity</Chip>}
            {item.staff_verify && <Chip icon="BadgeCheck" tone="text-emerald-700 bg-emerald-50">Staff verify</Chip>}
            {item.show_if && <Chip icon="GitBranch" tone="text-slate-500 bg-slate-100">Conditional</Chip>}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 pl-11 space-y-1.5 text-xs text-slate-500 bg-slate-50/40">
          <div><span className="font-semibold text-slate-400">Client-facing:</span> "{item.client_wording}"</div>
          {item.show_if && <div><span className="font-semibold text-slate-400">Shown when:</span> {item.show_if.q} = {String(item.show_if.eq)}</div>}
          <div className="flex gap-4">
            <span><span className="font-semibold text-slate-400">NA allowed:</span> {item.na_allowed ? "Yes" : "No"}</span>
            <span><span className="font-semibold text-slate-400">Required:</span> {item.required ? "Yes" : "No"}</span>
          </div>
          {item.notes && <div><span className="font-semibold text-slate-400">Internal:</span> {item.notes}</div>}
        </div>
      )}
    </div>
  );
}

export default function OrganizerMap() {
  const [meta, setMeta] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [activeKey, setActiveKey] = useState("individual_1040");

  useEffect(() => {
    api.get("/organizer-map/meta").then((r) => setMeta(r.data)).catch(() => toast.error("Could not load organizer map"));
  }, []);
  useEffect(() => {
    if (activeKey) api.get(`/organizer-map/templates/${activeKey}`).then((r) => setTpl(r.data)).catch(() => {});
  }, [activeKey]);

  if (!meta) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;
  const docTypes = meta.doc_types, addons = meta.addons;
  const profileLabels = {}; // field key already human-ish; keep raw

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="organizer-map-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Organizer Map</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">How each legacy organizer question maps into Tax Profile fields, document triggers, service add-ons, and complexity — the foundation for the Document Requirement Rules Engine. This is reference data; no document requests are generated here.</p>
        <div data-testid="om-authority-notice" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 max-w-2xl">
          <Icons.Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span><span className="font-semibold">Legacy reference catalogue — not runtime authority.</span> The live organizer model, Tax Profile mapping, and document triggers are governed elsewhere (organizer templates + Document Rules engine). This page documents the traditional paper organizer for reference only.</span>
        </div>
      </div>

      {/* template library */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="om-template-library">
        {meta.templates.map((t) => (
          <button key={t.key} onClick={() => setActiveKey(t.key)} data-testid={`om-template-${t.key}`}
            className={cn("text-left rounded-2xl border p-4 transition-all", activeKey === t.key ? "border-blue-400 ring-2 ring-blue-100 bg-blue-50/40" : "border-slate-200 hover:border-slate-300 bg-white")}>
            <div className="flex items-center gap-2"><Icons.LayoutTemplate className="h-5 w-5 text-blue-500" /><span className="font-semibold text-[#171B2E]">{t.name}</span></div>
            <div className="text-xs text-slate-400 mt-1">{t.use_case}</div>
            <div className="text-[11px] text-slate-400 mt-2">Applies to: {t.applies_to}</div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Chip icon="Layers">{t.stats.sections} sections</Chip>
              <Chip icon="ListChecks">{t.stats.items} questions</Chip>
              <Chip icon="FileText" tone="text-indigo-600 bg-indigo-50">{t.stats.doc_triggers} doc types</Chip>
              <Chip icon="PlusCircle" tone="text-fuchsia-600 bg-fuchsia-50">{t.stats.addon_triggers} add-ons</Chip>
            </div>
          </button>
        ))}
      </div>

      {tpl && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-400">Optional add-ons:</span>
            {tpl.optional_addons.map((a) => <Chip key={a} icon="PlusCircle" tone="text-fuchsia-600 bg-fuchsia-50">{addons[a] || a}</Chip>)}
          </div>

          {tpl.sections.map((s, i) => (
            <div key={s.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid={`om-section-${s.key}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-7 w-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <h3 className="font-heading text-base font-semibold text-[#171B2E]">{s.title}</h3>
                <span className="ml-auto text-xs text-slate-400">{s.items.length} question{s.items.length > 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-2">
                {s.items.map((it) => <ItemRow key={it.key} item={it} docTypes={docTypes} addons={addons} profileLabels={profileLabels} />)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
